import CssBaseline from '@mui/material/CssBaseline';
import GlobalStyles from '@mui/material/GlobalStyles';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { ErrorBoundary } from '@openx/components-core/lib/ErrorBoundary/ErrorBoundary';
import { LazyLoadable } from '@openx/components-core/lib/LazyLoadable/LazyLoadable';
import { SnackbarProvider, snakbarStyles } from '@openx/components-core/lib/Snackbar/SnackbarProvider';
import { theme } from '@openx/components-core/lib/theme/theme';
import { createQueryClient } from '@openx/constants/react-query';
import { globalStyles } from '@openx/constants/styles';
import { useRollbar } from '@openx/utils/hooks/rollbarIntegration/useRollbar';
import { QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Route, Routes } from 'react-router';
import { http } from './api';
import { LoginRoute } from './components/LoginRoute';
import { DISPATCHER_VERSION } from './config';

const DispatcherRedirect = LazyLoadable(() => import('./components/DispatcherRedirect'), 'DispatcherRedirect');
const Login = LazyLoadable(() => import('./modules/login/Login'), 'Login');
const ResponseOIDC = LazyLoadable(() => import('./modules/responseOIDC/ResponseOIDC'), 'ResponseOIDC');
const SsoRedirect = LazyLoadable(() => import('@openx/components-core/lib/SsoRedirect/SsoRedirect'), 'SsoRedirect');
const AuthProviderBase = LazyLoadable(
  () => import('@openx/components-core/lib/AuthProviderBase/AuthProviderBase'),
  'AuthProviderBase'
);
const PageNotFound = LazyLoadable(() => import('@openx/components-core/lib/PageNotFound/PageNotFound'), 'PageNotFound');
const DispatcherAccessForbiddenPage = LazyLoadable(
  () => import('./components/DispatcherAccessForbiddenPage'),
  'DispatcherAccessForbiddenPage'
);
const OIDCAuthProvider = LazyLoadable(
  () => import('@openx/components-core/lib/OIDCAuthProvider/OIDCAuthProvider'),
  'OIDCAuthProvider'
);

export function App() {
  const { initialize, rollbar } = useRollbar();
  const queryClient = useRef(createQueryClient(http));

  useEffect(() => {
    // biome-ignore lint/suspicious/noConsole: logging info in console
    console.info(`%cDispatcher version: ${DISPATCHER_VERSION}`, 'font-weight:bold;');
  }, []);

  useEffect(() => {
    initialize();
  }, [initialize]);

  return (
    <QueryClientProvider client={queryClient.current}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <SnackbarProvider>
            <GlobalStyles styles={[globalStyles, snakbarStyles(theme)]} />
            <CssBaseline />
            <ErrorBoundary rollbar={rollbar}>
              <Routes>
                <Route
                  element={
                    <OIDCAuthProvider>
                      <AuthProviderBase>
                        <DispatcherRedirect />
                      </AuthProviderBase>
                    </OIDCAuthProvider>
                  }
                  path="/"
                />
                <Route element={<ResponseOIDC />} path="/response-oidc" />
                <Route
                  element={
                    <OIDCAuthProvider>
                      <LoginRoute
                        login={<Login />}
                        redirect={
                          <AuthProviderBase>
                            <DispatcherRedirect />
                          </AuthProviderBase>
                        }
                      />
                    </OIDCAuthProvider>
                  }
                  path="/login"
                />
                <Route element={<SsoRedirect />} path="/sso-redirect" />
                <Route element={<DispatcherAccessForbiddenPage />} path="/forbidden" />
                <Route
                  element={
                    <OIDCAuthProvider>
                      <PageNotFound withHeader={false} />
                    </OIDCAuthProvider>
                  }
                  path="/*"
                />
              </Routes>
            </ErrorBoundary>
          </SnackbarProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    </QueryClientProvider>
  );
}
